import React, { useState, useEffect, useRef } from 'react';
import { Table, CreateTableData, UpdateTableData, GameType, TableStatus } from '@shared/types';
import { db } from '../../firebase/firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc, deleteDoc, writeBatch, query, orderBy, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { GAME_TYPES, TABLE_STATUSES } from '@shared/types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { EditablePosition } from './EditablePosition';

interface TableManagerProps {
  roomId: string;
  tables: Table[];
  onTableSelect: (table: Table | null) => void;
  selectedTable: Table | null;
}

export function TableManager({ roomId, tables, onTableSelect, selectedTable }: TableManagerProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitlistCounts, setWaitlistCounts] = useState<Map<string, number>>(new Map());
  const unsubscribeRefs = useRef<Map<string, Unsubscribe>>(new Map());

  const handleCreateTable = async (data: CreateTableData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Calculate next display_order by finding max display_order + 1
      const maxDisplayOrder = tables.length > 0 ? Math.max(...tables.map(t => t.display_order || 0)) : 0;
      const nextDisplayOrder = maxDisplayOrder + 1;
      
      // Use direct Firebase calls instead of shared utility
      const tablesRef = collection(db, 'rooms', roomId, 'tables');
      await addDoc(tablesRef, {
        ...data,
        status: data.status ?? 'Open',
        display_order: data.display_order ?? nextDisplayOrder,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      
      setShowCreateForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create table');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTable = async (tableId: string, data: UpdateTableData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use direct Firebase calls instead of shared utility
      const tableRef = doc(db, 'rooms', roomId, 'tables', tableId);
      await updateDoc(tableRef, {
        ...data,
        updated_at: serverTimestamp(),
      });
      
      setEditingTable(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update table');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTable = async (tableId: string) => {
    if (!confirm('Are you sure you want to delete this table?')) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Use direct Firebase calls instead of shared utility
      const tableRef = doc(db, 'rooms', roomId, 'tables', tableId);
      await deleteDoc(tableRef);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete table');
    } finally {
      setLoading(false);
    }
  };

  const handleDisplayOrderChange = async (tableId: string, newDisplayOrder: number) => {
    try {
      setLoading(true);
      setError(null);
      
      // Find the table being moved
      const movingTable = tables.find(t => t.id === tableId);
      if (!movingTable) {
        throw new Error('Table not found');
      }
      
      const oldDisplayOrder = movingTable.display_order || 999;
      
      // If moving to same position, do nothing
      if (oldDisplayOrder === newDisplayOrder) {
        setLoading(false);
        return;
      }
      
      // Use batch writes to reorder tables
      const batch = writeBatch(db);
      
      // Get all tables sorted by current display_order
      const sortedTables = [...tables].sort((a, b) => (a.display_order || 999) - (b.display_order || 999));
      
      // Update positions for all affected tables
      if (oldDisplayOrder < newDisplayOrder) {
        // Moving down: shift tables between old and new position up
        for (const table of sortedTables) {
          const tableDisplayOrder = table.display_order || 999;
          if (tableDisplayOrder > oldDisplayOrder && tableDisplayOrder <= newDisplayOrder) {
            const tableRef = doc(db, 'rooms', roomId, 'tables', table.id);
            batch.update(tableRef, { 
              display_order: tableDisplayOrder - 1,
              updated_at: serverTimestamp()
            });
          }
        }
      } else {
        // Moving up: shift tables between new and old position down
        for (const table of sortedTables) {
          const tableDisplayOrder = table.display_order || 999;
          if (tableDisplayOrder >= newDisplayOrder && tableDisplayOrder < oldDisplayOrder) {
            const tableRef = doc(db, 'rooms', roomId, 'tables', table.id);
            batch.update(tableRef, { 
              display_order: tableDisplayOrder + 1,
              updated_at: serverTimestamp()
            });
          }
        }
      }
      
      // Update the moved table's display_order
      const movingTableRef = doc(db, 'rooms', roomId, 'tables', tableId);
      batch.update(movingTableRef, { 
        display_order: newDisplayOrder,
        updated_at: serverTimestamp()
      });
      
      await batch.commit();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update display order');
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time listeners for waitlist counts
  useEffect(() => {
    if (!roomId || !db || tables.length === 0) {
      // Clean up all listeners if no tables
      unsubscribeRefs.current.forEach((unsubscribe) => unsubscribe());
      unsubscribeRefs.current.clear();
      setWaitlistCounts(new Map());
      return;
    }

    // Set up listeners for each table's waitlist
    const tableIds = new Set(tables.map(t => t.id));
    
    // Clean up listeners for tables that no longer exist
    unsubscribeRefs.current.forEach((unsubscribe, tableId) => {
      if (!tableIds.has(tableId)) {
        unsubscribe();
        unsubscribeRefs.current.delete(tableId);
      }
    });

    // Set up new listeners for tables that don't have one yet
    tables.forEach((table) => {
      if (unsubscribeRefs.current.has(table.id)) {
        // Listener already exists for this table
        return;
      }

      try {
        const waitlistRef = collection(db, 'rooms', roomId, 'tables', table.id, 'waitlist');
        const waitlistQuery = query(waitlistRef, orderBy('position', 'asc'));
        
        const unsubscribe = onSnapshot(
          waitlistQuery,
          (snapshot) => {
            // Count only entries with status === 'waiting'
            const count = snapshot.docs.filter(doc => {
              const data = doc.data();
              return data.status === 'waiting';
            }).length;

            setWaitlistCounts((prev) => {
              const newMap = new Map(prev);
              newMap.set(table.id, count);
              return newMap;
            });
          },
          (err) => {
            // If position query fails, try fallback query with added_at
            console.warn(`Position query failed for table ${table.id}, trying fallback:`, err);
            
            // Clean up the original unsubscribe
            unsubscribe();
            
            const fallbackQuery = query(waitlistRef, orderBy('added_at', 'asc'));
            const fallbackUnsubscribe = onSnapshot(
              fallbackQuery,
              (snapshot) => {
                // Count only entries with status === 'waiting'
                const count = snapshot.docs.filter(doc => {
                  const data = doc.data();
                  return data.status === 'waiting';
                }).length;

                setWaitlistCounts((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(table.id, count);
                  return newMap;
                });
              },
              (fallbackErr) => {
                // If fallback also fails, set count to 0
                console.warn(`Fallback query also failed for table ${table.id}:`, fallbackErr);
                setWaitlistCounts((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(table.id, 0);
                  return newMap;
                });
              }
            );
            
            // Store the fallback unsubscribe function
            unsubscribeRefs.current.set(table.id, fallbackUnsubscribe);
          }
        );

        unsubscribeRefs.current.set(table.id, unsubscribe);
      } catch (err) {
        // Handle cases where waitlist collection doesn't exist yet
        console.warn(`Error setting up waitlist listener for table ${table.id}:`, err);
        setWaitlistCounts((prev) => {
          const newMap = new Map(prev);
          newMap.set(table.id, 0);
          return newMap;
        });
      }
    });

    // Cleanup function
    return () => {
      unsubscribeRefs.current.forEach((unsubscribe) => unsubscribe());
      unsubscribeRefs.current.clear();
    };
  }, [roomId, tables]);

  const getStatusBadge = (status: string) => {
    const baseClasses = 'badge';
    switch (status) {
      case 'Open':
        return `${baseClasses} badge-success`;
      case 'Running':
        return `${baseClasses} badge-info`;
      case 'Closed':
        return `${baseClasses} badge-danger`;
      case 'Interest':
        return `${baseClasses} badge-warning`;
      default:
        return `${baseClasses} badge-info`;
    }
  };

  return (
    <ErrorBoundary>
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Tables</h2>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
            Add Table
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {showCreateForm && (
          <CreateTableForm
            roomId={roomId}
            onSubmit={handleCreateTable}
            onCancel={() => setShowCreateForm(false)}
            loading={loading}
          />
        )}

        {editingTable && (
          <EditTableForm
            table={editingTable}
            onSubmit={(data) => handleUpdateTable(editingTable.id, data)}
            onCancel={() => setEditingTable(null)}
            loading={loading}
          />
        )}

        {tables.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No tables created yet. Add your first table!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tables
              .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
              .map((table) => (
              <div
                key={table.id}
                className={`card card-hover cursor-pointer ${
                  selectedTable?.id === table.id ? 'ring-2 ring-primary-500' : ''
                }`}
                onClick={() => onTableSelect(table)}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{table.name}</h3>
                  <div className="flex flex-col items-end space-y-1">
                    <span className={getStatusBadge(table.status)}>
                      {table.status}
                    </span>
                    <EditablePosition
                      position={table.display_order || 999}
                      maxPosition={tables.length}
                      onUpdate={(newPosition) => handleDisplayOrderChange(table.id, newPosition)}
                      loading={loading}
                    />
                  </div>
                </div>
                
                <div className="space-y-2 text-sm text-gray-600">
                  <p><span className="font-medium">Game:</span> {table.game_type}</p>
                  <p><span className="font-medium">Stakes:</span> {table.stakes}</p>
                  <p><span className="font-medium">Tables:</span> {table.number_of_tables}</p>
                  <p><span className="font-medium">Waitlist:</span> {waitlistCounts.get(table.id) ?? 0}</p>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTable(table);
                    }}
                    className="btn btn-outline btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTable(table.id);
                    }}
                    className="btn btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

// Create Table Form Component
interface CreateTableFormProps {
  roomId: string;
  onSubmit: (data: CreateTableData) => void;
  onCancel: () => void;
  loading: boolean;
}

function CreateTableForm({ roomId, onSubmit, onCancel, loading }: CreateTableFormProps) {
  const [formData, setFormData] = useState<CreateTableData & { status: TableStatus }>({
    name: '',
    game_type: 'NLH',
    stakes: '$1/$3',
    number_of_tables: 1,
    status: 'Open',
  });
  const [templates, setTemplates] = useState<Array<{ id: string; name: string; game_type: string; stakes: string; number_of_tables: number; status: string; custom_game_type?: string }>>([]);

  // Fetch templates for the "Load from Template" dropdown
  useEffect(() => {
    if (!roomId || !db) return;
    const templatesRef = collection(db, 'rooms', roomId, 'table_templates');
    const unsubscribe = onSnapshot(templatesRef, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      setTemplates(data);
    });
    return () => unsubscribe();
  }, [roomId]);

  const handleTemplateSelect = (templateId: string) => {
    if (!templateId) return;
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        name: template.name,
        game_type: template.game_type as any,
        stakes: template.stakes,
        number_of_tables: template.number_of_tables,
        status: template.status as TableStatus,
        ...(template.custom_game_type ? { custom_game_type: template.custom_game_type } : {}),
      });
    }
  };

  const handleStatusChange = (newStatus: TableStatus) => {
    const updatedData = { ...formData, status: newStatus };
    
    // If status is Interest or Closed, automatically set number_of_tables to 0
    if (newStatus === 'Interest' || newStatus === 'Closed') {
      updatedData.number_of_tables = 0;
    } else if (newStatus === 'Open' || newStatus === 'Running') {
      // Ensure at least 1 table for Open/Running status
      updatedData.number_of_tables = Math.max(1, updatedData.number_of_tables);
    }
    
    setFormData(updatedData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Pass the complete formData including status
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Table</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Template Selector — only shown if templates exist */}
          {templates.length > 0 && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <label className="label text-blue-900">Load from Template</label>
              <select
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="input"
                defaultValue=""
              >
                <option value="">— Start from scratch —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.game_type} · {t.stakes})</option>
                ))}
              </select>
              <p className="text-xs text-blue-600 mt-1">Pre-fills fields — you can still edit before creating</p>
            </div>
          )}

          <div>
            <label className="label">Table Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="e.g., Table 1"
              required
            />
          </div>

          <div>
            <label className="label">Game Type</label>
            <select
              value={formData.game_type}
              onChange={(e) => setFormData({ ...formData, game_type: e.target.value as GameType })}
              className="input"
            >
              {GAME_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Stakes</label>
            <input
              type="text"
              value={formData.stakes}
              onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
              className="input"
              placeholder="e.g., $1/$3"
              required
            />
          </div>

          <div>
            <label className="label">Status</label>
            <select
              value={formData.status}
              onChange={(e) => handleStatusChange(e.target.value as TableStatus)}
              className="input"
            >
              {TABLE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {(formData.status === 'Open' || formData.status === 'Running') && (
            <div>
              <label className="label">Number of Tables</label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.number_of_tables}
                onChange={(e) => setFormData({ ...formData, number_of_tables: parseInt(e.target.value) })}
                className="input"
                required
              />
            </div>
          )}

          {(formData.status === 'Interest' || formData.status === 'Closed') && (
            <div>
              <label className="label">Number of Tables</label>
              <input
                type="number"
                min="0"
                max="0"
                value="0"
                disabled
                className="input bg-gray-100"
              />
              <p className="text-sm text-gray-500 mt-1">
                Tables are automatically set to 0 for {formData.status.toLowerCase()} status
              </p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Create Table
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Table Form Component
interface EditTableFormProps {
  table: Table;
  onSubmit: (data: UpdateTableData) => void;
  onCancel: () => void;
  loading: boolean;
}

function EditTableForm({ table, onSubmit, onCancel, loading }: EditTableFormProps) {
  const [formData, setFormData] = useState<UpdateTableData>({
    name: table.name,
    game_type: table.game_type,
    stakes: table.stakes,
    number_of_tables: table.number_of_tables,
    status: table.status,
  });

  const handleStatusChange = (newStatus: TableStatus) => {
    const updatedData = { ...formData, status: newStatus };
    
    // If status is Interest or Closed, automatically set number_of_tables to 0
    if (newStatus === 'Interest' || newStatus === 'Closed') {
      updatedData.number_of_tables = 0;
    } else if (newStatus === 'Open' || newStatus === 'Running') {
      // If changing to Open/Running and number_of_tables is 0, default to 1
      // User can still manually change it to 0 if needed (validation will show error)
      if (!updatedData.number_of_tables || updatedData.number_of_tables === 0) {
        updatedData.number_of_tables = 1;
      }
    }
    
    setFormData(updatedData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Table</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Table Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Game Type</label>
            <select
              value={formData.game_type}
              onChange={(e) => setFormData({ ...formData, game_type: e.target.value as GameType })}
              className="input"
            >
              {GAME_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Stakes</label>
            <input
              type="text"
              value={formData.stakes}
              onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Status</label>
            <select
              value={formData.status}
              onChange={(e) => handleStatusChange(e.target.value as TableStatus)}
              className="input"
            >
              {TABLE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {(formData.status === 'Open' || formData.status === 'Running') && (
            <div>
              <label className="label">Number of Tables</label>
              <input
                type="number"
                min="0"
                max="50"
                value={formData.number_of_tables ?? 1}
                onChange={(e) => setFormData({ ...formData, number_of_tables: parseInt(e.target.value) || 0 })}
                className="input"
                required
              />
            </div>
          )}

          {(formData.status === 'Interest' || formData.status === 'Closed') && (
            <div>
              <label className="label">Number of Tables</label>
              <input
                type="number"
                min="0"
                max="0"
                value="0"
                disabled
                className="input bg-gray-100"
              />
              <p className="text-sm text-gray-500 mt-1">
                Tables are automatically set to 0 for {formData.status.toLowerCase()} status
              </p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Update Table
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

